import { Component, computed, inject, input, model, OnInit, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { isUndefined, sortBy } from 'lodash';
import { finalize } from 'rxjs/operators';
import { tryGetErrorMessage } from 'src/app/error.utils';
import { customSearchForIdAndName } from 'src/app/search.utils';
import { UiModule } from '../../../components/ui.module';
import { BranchService } from '../../../service/branch.service';
import { UserLoginService } from '../../../service/user-login.service';
import { Branch, GetBranchesRequest } from '../../../types';

type SelectableBranch = Branch & { disabled?: boolean };

@Component({
  selector: 'app-page-select-branch',
  templateUrl: './page-select-branch.component.html',
  styleUrl: './page-select-branch.component.scss',
  standalone: true,
  imports: [UiModule, NgSelectModule, FormsModule],
})
export class PageSelectBranchComponent implements OnInit {
  private branchService = inject(BranchService);
  private userService = inject(UserLoginService);

  public selected = model<Branch[] | Branch | undefined>(undefined);

  public autoSelectDefaultBranch = input<boolean>(false);
  public autoSelectBranchIds = input<number[]>(undefined);
  public disabled = input<boolean>(false);
  public multi = input<boolean>(false);
  public label = input<string>('Select Branch');
  public placeholder = input<string>('Select branch...');
  public overrideBranches = input<Branch[]>([]);
  public showIncidentIcon = input<boolean>(false);
  public showExternalId = input<boolean>(false);
  public doNaturalSort = input<boolean>(false);
  public branchRequestParams = input<Partial<GetBranchesRequest>>({});
  public branchFilterFn = input<((location: Branch) => boolean) | undefined>(() => true);

  public defaultSelected = output<Branch>();

  public isLoading = signal<boolean>(false);
  public error = signal<string | undefined>(undefined);
  public branches = signal<Branch[]>([]);
  public customSearchFn = customSearchForIdAndName;

  public dropdownBranches = computed<SelectableBranch[]>(() => {
    const branches = this.overrideBranches().length > 0 ? this.overrideBranches() : this.branches();
    return branches.map((branch) => ({
      ...branch,
      disabled: !this.userService.currentUserIsAdmin() && (branch.keystore?.suspend_access ?? false),
    }));
  });
  public visibleBranches = computed(() =>
    (this.doNaturalSort() ? this.dropdownBranches() : sortBy(this.dropdownBranches(), (b) => b.name.trim())).filter(this.branchFilterFn()),
  );

  public ngOnInit() {
    this.getAllBranches();
  }

  private getAllBranches() {
    if (this.overrideBranches().length > 0) return;

    this.isLoading.set(true);
    this.branchService
      .getBranches(this.branchRequestParams())
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe(
        (result) => this.setBranches(result.items),
        (err) => this.error.set(tryGetErrorMessage(err)),
      );
  }

  public setBranches(branches: Branch[]) {
    this.branches.set(branches);

    if (this.autoSelectDefaultBranch()) {
      this.selectDefaultBranch();
    }

    const loadIds = this.autoSelectBranchIds();
    if (!isUndefined(loadIds) && loadIds.length > 0) {
      this.selectBranches(loadIds.map((i) => +i));
    }
  }

  public reset() {
    this.selected.set(undefined);
  }

  public selectDefaultBranch() {
    if (this.branches().length === 0) return;

    const defaultBranch = this.visibleBranches().find((branch) => !branch.disabled);
    if (!defaultBranch) return;

    this.selectBranches([defaultBranch.id]);
    this.defaultSelected.emit(defaultBranch);
  }

  public selectBranches(branchIds: number[]) {
    const foundBranches = this.dropdownBranches().filter((b) => branchIds.includes(b.id) && !b.disabled);

    this.selected.set(this.multi() ? foundBranches : foundBranches[0]);
  }
}
